import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown } from '@fortawesome/free-solid-svg-icons';

const ProjectPreparation = () => {
  const [expanded, setExpanded] = useState({
    typesOfActivities: true,
    typesOfProjects: false,
    schemesOfDevelopment: false,
    sourcesOfWater: false,
    typesOfInfrastructure: false,
  });

  const toggleSection = (section) => {
    setExpanded((prev) => {
      const newState = {
        typesOfActivities: false,
        typesOfProjects: false,
        schemesOfDevelopment: false,
        sourcesOfWater: false,
        typesOfInfrastructure: false,
        [section]: !prev[section],
      };
      return newState;
    });
  };

  return (
    <Wrapper>
      <Header>
        <TitleP>Project Preparation</TitleP>
        <IntroText>
          Activities are consistently carried out to ensure a broad selection base for project implementation. These activities include project identification, investigations, feasibility studies, plan formulation, project packaging, and detailed engineering.
        </IntroText>
      </Header>

      <Card>
        <Title>Implementation of Irrigation Projects</Title>

        <Section>
          <Subtitle onClick={() => toggleSection('typesOfActivities')} aria-expanded={expanded.typesOfActivities}>
            Types of Activities <Arrow expanded={expanded.typesOfActivities} />
          </Subtitle>
          {expanded.typesOfActivities && (
            <Content expanded={expanded.typesOfActivities}>
              <ListItem index={0}>
                <strong>Generation of New Areas:</strong> 
                Construction of facilities and structures to provide irrigation and drainage services to currently rainfed areas, idle lands, and other identified potential irrigable areas.
              </ListItem>
              <ListItem index={1}>
                <strong>Rehabilitation and Improvement of Irrigation Systems:</strong> 
                Activities to improve the efficiency and effectiveness of water delivery to existing irrigated areas by addressing wear and tear of facilities.
              </ListItem>
              <ListItem index={2}>
                <strong>Restoration Works:</strong> 
                Bringing non-functioning service areas in NIS and CIS back into operation, including reconstruction of facilities and provision of additional structures.
              </ListItem>
            </Content>
          )}
        </Section>

        <Section>
          <Subtitle onClick={() => toggleSection('typesOfProjects')} aria-expanded={expanded.typesOfProjects}>
            Types of Projects <Arrow expanded={expanded.typesOfProjects} />
          </Subtitle>
          {expanded.typesOfProjects && (
            <Content expanded={expanded.typesOfProjects}>
              <ListItem>
                <strong>Single-Purpose Projects:</strong> Projects designed solely for irrigation purposes.
              </ListItem>
              <ListItem>
                <strong>Multipurpose Projects:</strong> Projects that include additional components like hydropower generation, fisheries, domestic water supply, flood control, recreation, and tourism.
              </ListItem>
              <ListItem>
                <strong>Irrigation Packages:</strong> Implementation of two or more projects in different locations, combining generation of new areas, rehabilitation, and restoration of NIS and CIS.
              </ListItem>
            </Content>
          )}
        </Section>

        <Section>
          <Subtitle onClick={() => toggleSection('schemesOfDevelopment')} aria-expanded={expanded.schemesOfDevelopment}>
            Schemes of Development <Arrow expanded={expanded.schemesOfDevelopment} />
          </Subtitle>
          {expanded.schemesOfDevelopment && (
            <Content expanded={expanded.schemesOfDevelopment}>
              <ListItem>
                <strong>Run-of-the-River or Diversion Projects:</strong> Drawing water directly from rivers or streams under controlled conditions.
              </ListItem>
              <ListItem>
                <strong>Storage or Reservoir Projects:</strong> Construction of dams to store water, which is then released as needed. These projects can be large or small-scale and may include other components like hydropower or flood control.
              </ListItem>
              <ListItem>
                <strong>Pump Projects:</strong> Lifting water from underground or surface water sources to irrigate higher elevation areas or supplement water supply.
              </ListItem>
            </Content>
          )}
        </Section>

        <Section>
          <Subtitle onClick={() => toggleSection('sourcesOfWater')} aria-expanded={expanded.sourcesOfWater}>
            Sources of Water for Irrigation <Arrow expanded={expanded.sourcesOfWater} />
          </Subtitle>
          {expanded.sourcesOfWater && (
            <Content expanded={expanded.sourcesOfWater}>
              <ListItem>
                <strong>Surface Water:</strong> Water from rivers, streams, and lakes.
              </ListItem>
              <ListItem>
                <strong>Groundwater:</strong> Water pumped through shallow and deep tubewells.
              </ListItem>
            </Content>
          )}
        </Section>

        <Section>
          <Subtitle onClick={() => toggleSection('typesOfInfrastructure')} aria-expanded={expanded.typesOfInfrastructure}>
            Types of Infrastructure <Arrow expanded={expanded.typesOfInfrastructure} />
          </Subtitle>
          {expanded.typesOfInfrastructure && (
            <Content expanded={expanded.typesOfInfrastructure}>
              <ListItem>
                <strong>Irrigation Structures:</strong> Includes dams, intake gates, sluice gates, headgates, long-crest weirs, and end checks.
              </ListItem>
              <ListItem>
                <strong>Canal Systems:</strong> Main canals, laterals, sub-laterals, farm ditches, flumes, siphons, and turnouts.
              </ListItem>
              <ListItem>
                <strong>Drainage Systems:</strong> Systems to manage excess water.
              </ListItem>
              <ListItem>
                <strong>Access and Service Roads:</strong> Including bridges where necessary to provide access to irrigation areas.
              </ListItem>
            </Content>
          )}
        </Section>
      </Card>
    </Wrapper>
  );
};

export default ProjectPreparation;

// Styled Components
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Wrapper = styled.div`
  padding: 20px;
  background-color: #ffffff;
  font-family: 'Segoe UI', sans-serif;
  margin-top: 60px; /* ✅ Add this line to push content down from navbar */
`;

const Header = styled.div`
  max-width: 1000px;
  margin: 0 auto 20px;
  text-align: center;
`;

const TitleP = styled.h1`
  font-size: 24px;
  font-weight: bold;
  color: #ffffff;
  background-color: rgb(5, 107, 10);
  padding: 10px;
  margin: 0;
`;

const IntroText = styled.p`
  font-size: 14px;
  color: #444;
  line-height: 1.5;
  margin: 10px 0;
`;

const Card = styled.section`
  max-width: 1000px;
  margin: 0 auto;
  background-color: #ffffff;
  box-shadow: -10px 0 20px rgba(0, 0, 0, 0.99); /* Thicker left-side shadow */
  padding: 10px;
`;

const Section = styled.div`
  margin-bottom: 10px;
`;

const Title = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #222;
  margin: 10px 0;
`;

const Subtitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #007B5E;
  margin: 5px 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 15px;
  background-color: #e6f0ea;
  border-radius: 5px;
  transition: background-color 0.3s ease, color 0.3s ease;

  &:hover {
    color: #005b45;
    background-color: #d9e6e0;
  }
`;

const Arrow = styled(FontAwesomeIcon).attrs({ icon: faAngleDown })`
  font-size: 20px;
  color: #007B5E;
  transition: transform 0.4s ease, color 0.3s ease;
  transform: ${({ expanded }) => (expanded ? 'rotate(180deg)' : 'rotate(0deg)')};
  margin-left: 10px;

  ${Subtitle}:hover & {
    color: #005b45;
  }
`;

const Content = styled.div`
  background-color: ${props => (props.expanded ? 'rgb(134, 138, 134)' : '#f0f0f0')};
  padding: 5px 5px;
  margin: 10px 10px 5px 10px;
  border-radius: 12px;
  transition: background-color 0.5s ease, transform 0.3s ease;
  box-shadow: ${props => (props.expanded ? '0 4px 12px rgba(0,0,0,0.15)' : 'none')};
  animation: ${fadeIn} 0.4s ease;
  background: rgb(5, 107, 10);
  backdrop-filter: blur(6px);
`;

const ListItem = styled.li`
  background: #f9fdfc;
  border-left: 6px solid
    ${({ index }) =>
      index === 0 ? '#33bb8c' :
      index === 1 ? '#63bfa7' :
      '#88d2c6'};
  padding: 10px 15px;
  margin-bottom: 12px;
  border-radius: 6px;
  font-size: 14px;
  color: #333;
  list-style-type: none;
  animation: ${fadeIn} 0.4s ease;

  strong {
    color: #00695c;
    font-weight: 600;
    display: block;
    margin-bottom: 3px;
  }
`;